'use client';

import {
  FileFormat,
  IngestionSourceType,
  PartitionFormat,
  PartitionSegmentType,
  PartitionType,
  type BucketPathPartitionSpec,
  type GcsSourceProperties,
  type IngestionPipeline,
  type S3SourceProperties,
  type UpdateIngestionPipelineInput,
} from '@repo/api-gw-sdk';

import { Wizard } from '@/components/wizard/Wizard';
import { useDAL } from '@/contexts/useDAL';
import { useWorkspace } from '@/contexts/useWorkspace';

import { IngestionPipelineCreationFlow } from './IngestionPipelineCreationFlow';
import {
  mapSegments,
  type IngestionPipelineWizardState,
} from './IngestionPipelineWizardState';

interface UpdateIngestionPipelineWizardPanelProps {
  workspaceId: string;
  pipeline: IngestionPipeline;
  onAbort: () => void;
  onUpdated: () => void;
}

function pipelineToWizardState(
  pipeline: IngestionPipeline
): IngestionPipelineWizardState {
  const s3 = pipeline.s3SourceProperties;
  const gcs = pipeline.gcsSourceProperties;
  const spec = s3?.bucketPathPartitionSpec ?? gcs?.bucketPathPartitionSpec;

  return {
    name: pipeline.name,
    namespace: pipeline.namespace,
    tableName: pipeline.tableName,
    sourceType: pipeline.sourceType,
    s3Bucket: s3?.bucket ?? '',
    s3Region: s3?.region ?? '',
    s3OwnerAccountId: s3?.ownerAccountId ?? '',
    gcsBucket: gcs?.bucket ?? '',
    gcsProjectId: gcs?.projectId ?? '',
    gcsLocation: gcs?.location ?? '',
    fileFormat:
      s3?.fileFormat ?? gcs?.fileFormat ?? FileFormat.FILE_FORMAT_JSON,
    partitionSpec: {
      separator: spec?.separator ?? '/',
      segments: (spec?.segments ?? []).map((seg, i) => ({
        id: `seg-${i}`,
        segmentType: seg.segmentType,
        segmentStringValue: seg.segmentStringValue ?? '',
        segmentPartitionName: seg.segmentPartitionName ?? '',
        segmentPartitionType:
          seg.segmentPartitionType ??
          PartitionSegmentType.PARTITION_SEGMENT_TYPE_SECONDARY,
        segmentPartitionFormat:
          seg.segmentPartitionFormat ?? PartitionFormat.PARTITION_FORMAT_HIVE,
      })),
    },
    partitionBuilderMode: 'build',
    samplePath: '',
    isUpdateMode: true,
  };
}

export function UpdateIngestionPipelineWizardPanel({
  workspaceId,
  pipeline,
  onAbort,
  onUpdated,
}: UpdateIngestionPipelineWizardPanelProps) {
  const dal = useDAL();
  const { snackbar } = useWorkspace();

  const save = async (
    state: IngestionPipelineWizardState
  ): Promise<boolean> => {
    const bucketPathPartitionSpec: BucketPathPartitionSpec = {
      separator: state.partitionSpec.separator,
      segments: mapSegments(state.partitionSpec.segments),
    };

    const request: UpdateIngestionPipelineInput = {
      name: state.name,
      namespace: state.namespace,
      tableName: state.tableName,
      sourceType: state.sourceType,
    };

    if (state.sourceType === IngestionSourceType.INGESTION_SOURCE_TYPE_S3) {
      const s3SourceProperties: S3SourceProperties = {
        bucket: state.s3Bucket,
        prefix: '',
        region: state.s3Region,
        ownerAccountId: state.s3OwnerAccountId,
        fileFormat: state.fileFormat,
        partitionType: PartitionType.PARTITION_TYPE_BUCKET_PATH,
        bucketPathPartitionSpec,
      };
      request.s3SourceProperties = s3SourceProperties;
    } else {
      const gcsSourceProperties: GcsSourceProperties = {
        bucket: state.gcsBucket,
        prefix: '',
        projectId: state.gcsProjectId,
        location: state.gcsLocation,
        fileFormat: state.fileFormat,
        partitionType: PartitionType.PARTITION_TYPE_BUCKET_PATH,
        bucketPathPartitionSpec,
      };
      request.gcsSourceProperties = gcsSourceProperties;
    }

    try {
      await dal.lakehouse.updateIngestionPipeline(
        workspaceId,
        pipeline.id,
        request
      );
      onUpdated();
      return true;
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to update pipeline';
      snackbar.showErrorMessage(message);
      return false;
    }
  };

  return (
    <Wizard<IngestionPipelineWizardState>
      onAbort={onAbort}
      hideBackArrow={true}
      onFinish={save}
      flow={[IngestionPipelineCreationFlow]}
      initialState={pipelineToWizardState(pipeline)}
      title='Update ingestion pipeline'
      stepperLabels={['Pipeline Details', 'Source', 'Partitioning', 'Review']}
    />
  );
}
